import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, distinctUntilChanged, map } from 'rxjs';

export enum DeviceOrientation {
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait',
  UNKNOWN = 'unknown',
}

export enum ScreenSize {
  XS = 0,
  SM = 640,
  MD = 768,
  LG = 1024,
  XL = 1280,
  XXL = 1536,
}

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  private breakpointObserver = inject(BreakpointObserver);

  /**
   * Emits the current screen width in pixels.
   */
  readonly screenSize$ = this.observeScreenSize();

  readonly isScreenXS$ = this.screenSize$.pipe(
    map((screenSize) => screenSize >= ScreenSize.XS),
    distinctUntilChanged(),
  );
  readonly isScreenXS = toSignal(this.isScreenXS$, { initialValue: false });

  readonly isScreenSM$ = this.screenSize$.pipe(
    map((screenSize) => screenSize >= ScreenSize.SM),
    distinctUntilChanged(),
  );
  readonly isScreenSM = toSignal(this.isScreenSM$, { initialValue: false });

  readonly isScreenMD$ = this.screenSize$.pipe(
    map((screenSize) => screenSize >= ScreenSize.MD),
    distinctUntilChanged(),
  );
  readonly isScreenMD = toSignal(this.isScreenMD$, { initialValue: false });

  readonly isScreenLG$ = this.screenSize$.pipe(
    map((screenSize) => screenSize >= ScreenSize.LG),
    distinctUntilChanged(),
  );
  readonly isScreenLG = toSignal(this.isScreenLG$, { initialValue: false });

  readonly isScreenXL$ = this.screenSize$.pipe(
    map((screenSize) => screenSize >= ScreenSize.XL),
    distinctUntilChanged(),
  );
  readonly isScreenXL = toSignal(this.isScreenXL$, { initialValue: false });

  readonly isScreenXXL$ = this.screenSize$.pipe(
    map((screenSize) => screenSize >= ScreenSize.XXL),
    distinctUntilChanged(),
  );
  readonly isScreenXXL = toSignal(this.isScreenXXL$, { initialValue: false });

  /**
   * We define mobile screens to be all screens below screen size MD.
   */
  readonly isMobile$ = this.isScreenMD$.pipe(
    map((isScreenMD) => !isScreenMD),
    distinctUntilChanged(),
  );
  readonly isMobile = toSignal(this.isMobile$, { initialValue: false });

  observeOrientation(): Observable<DeviceOrientation> {
    const orientationQueriesMap = new Map([
      [this.orientationQuery(DeviceOrientation.LANDSCAPE), DeviceOrientation.LANDSCAPE],
      [this.orientationQuery(DeviceOrientation.PORTRAIT), DeviceOrientation.PORTRAIT],
    ]);

    return this.breakpointObserver.observe([...orientationQueriesMap.keys()]).pipe(
      map((result) => {
        for (const query of orientationQueriesMap.keys()) {
          if (result.breakpoints[query]) {
            return orientationQueriesMap.get(query) ?? DeviceOrientation.UNKNOWN;
          }
        }

        return DeviceOrientation.UNKNOWN;
      }),
    );
  }

  observeScreenSize(): Observable<ScreenSize> {
    const minWidthQueriesMap = new Map([
      // Screen sizes must be sorted in descending order
      [this.minWidthQuery(ScreenSize.XXL), ScreenSize.XXL],
      [this.minWidthQuery(ScreenSize.XL), ScreenSize.XL],
      [this.minWidthQuery(ScreenSize.LG), ScreenSize.LG],
      [this.minWidthQuery(ScreenSize.MD), ScreenSize.MD],
      [this.minWidthQuery(ScreenSize.SM), ScreenSize.SM],
      [this.minWidthQuery(ScreenSize.XS), ScreenSize.XS],
    ]);

    return this.breakpointObserver.observe([...minWidthQueriesMap.keys()]).pipe(
      map((result) => {
        for (const query of minWidthQueriesMap.keys()) {
          if (result.breakpoints[query]) {
            return minWidthQueriesMap.get(query) ?? ScreenSize.XS;
          }
        }

        return ScreenSize.XS;
      }),
    );
  }

  private minWidthQuery(minWidth: number): string {
    return `(min-width: ${minWidth}px)`;
  }

  private orientationQuery(orientation: DeviceOrientation): string {
    return `(orientation: ${orientation}px)`;
  }
}
